import { createContext, useContext, useEffect, useState } from "react";
import { authLogin, authMe } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "pergamon_admin_token";
const MOCK_TOKEN_PREFIX = "mock-admin-";

// Hardcoded demo credentials for static deploys (Netlify, etc.) with no backend.
const MOCK_EMAIL = "admin@pergamon-agritech.com";
const MOCK_PASSWORD = "Pergamon2026!";
const MOCK_USER = { email: MOCK_EMAIL, role: "admin", name: "Admin" };

const isMockToken = (t) => typeof t === "string" && t.startsWith(MOCK_TOKEN_PREFIX);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=unknown, false=anon, obj=authed
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    if (isMockToken(token)) {
      setUser(MOCK_USER);
      setReady(true);
      return;
    }
    authMe()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    // 1) Try real backend first — if reachable, honor real auth
    try {
      const data = await authLogin(normalizedEmail, password);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      // 2) If backend unreachable OR returned error, fall back to hardcoded mock check
      const noBackend =
        !err?.response || err?.code === "ERR_NETWORK" || err?.message === "Network Error";
      if (
        (noBackend || err?.response?.status >= 500) &&
        normalizedEmail === MOCK_EMAIL &&
        password === MOCK_PASSWORD
      ) {
        const token = `${MOCK_TOKEN_PREFIX}${Date.now()}`;
        localStorage.setItem(TOKEN_KEY, token);
        setUser(MOCK_USER);
        return MOCK_USER;
      }
      // Also allow mock login when backend explicitly rejects but creds match hardcoded
      // (useful for demos where backend is up but DB missing seed)
      if (
        err?.response?.status === 401 &&
        normalizedEmail === MOCK_EMAIL &&
        password === MOCK_PASSWORD
      ) {
        const token = `${MOCK_TOKEN_PREFIX}${Date.now()}`;
        localStorage.setItem(TOKEN_KEY, token);
        setUser(MOCK_USER);
        return MOCK_USER;
      }
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
